import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Calendar, Award, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';

const getGradeStyle = (grade) => {
  const gradeUpper = grade?.toUpperCase() || '';
  if (gradeUpper === 'HIGH') return 'grade-high';
  if (gradeUpper === 'MODERATE') return 'grade-moderate';
  if (gradeUpper === 'LOW') return 'grade-low';
  return 'grade-very-low';
};

const getGradeLabel = (grade) => {
  const gradeUpper = grade?.toUpperCase() || 'UNKNOWN';
  return gradeUpper.replace('_', ' ');
};

// Fallback: Google Scholar search if no verified DOI
// Uses only key terms (removes common academic words) for better search results
const getScholarUrl = (studyName, source, year) => {
  if (!studyName) return null;
  
  // Common words to remove for cleaner Scholar search
  const stopwords = new Set([
    'the', 'a', 'an', 'of', 'in', 'for', 'and', 'with', 'on', 'to', 'from', 'by',
    'study', 'trial', 'analysis', 'review', 'systematic', 'meta', 'randomized',
    'controlled', 'clinical', 'effect', 'effects', 'treatment', 'therapy',
    'patients', 'patient', 'outcomes', 'outcome', 'results', 'versus', 'vs',
    'among', 'between', 'using', 'based', 'associated', 'association', 'risk',
    'double', 'blind', 'placebo', 'open', 'label', 'phase', 'multicenter',
    'prospective', 'retrospective', 'cohort', 'case', 'control', 'group'
  ]);
  
  // Extract key words (drug names, conditions, specific terms)
  // Remove punctuation and split into words
  const cleanedName = studyName.replace(/[^\w\s]/g, ' ');
  const words = cleanedName.toLowerCase().split(/\s+/);
  const keyWords = words.filter(word => {
    // Keep words that are:
    // - Not stopwords
    // - At least 4 characters (likely meaningful)
    // - Or contain numbers (dosages, years)
    if (word.length < 4) return false;
    if (stopwords.has(word)) return false;
    return true;
  });
  
  // Take first 5-6 key words to avoid over-restricting search
  const searchTerms = keyWords.slice(0, 6).join(' ');
  
  // Add source journal if it's a well-known one (helps narrow results)
  let query = searchTerms;
  const sourceLower = (source || '').toLowerCase();
  if (sourceLower.includes('lancet') || sourceLower.includes('nejm') || 
      sourceLower.includes('jama') || sourceLower.includes('bmj')) {
    query += ` ${source}`;
  }
  
  // Add year if available (helps find the right paper)
  if (year && year !== 'N/A' && /^\d{4}$/.test(year)) {
    query += ` ${year}`;
  }
  
  return `https://scholar.google.com/scholar?q=${encodeURIComponent(query.trim())}`;
};

const EvidenceCard = ({ card }) => {
  // Use verified DOI URL if available, otherwise fallback to Scholar search
  const verifiedUrl = card.verified_url;
  const scholarUrl = getScholarUrl(card.study_name, card.source, card.year);
  const isVerified = card.verified === true;
  const confidence = card.confidence || 'unknown';
  
  // Primary link is verified DOI, fallback is Scholar
  const primaryUrl = verifiedUrl || scholarUrl;
  const linkLabel = verifiedUrl ? 'View Source' : 'Find on Scholar';
  
  // Get confidence color
  const getConfidenceColor = () => {
    if (!isVerified) return '';
    if (confidence === 'high') return 'text-emerald-500 dark:text-emerald-400';
    if (confidence === 'medium') return 'text-amber-500 dark:text-amber-400';
    return 'text-slate-400 dark:text-slate-500';
  };
  
  const getConfidenceTitle = () => {
    if (!isVerified) return 'Citation not verified';
    if (confidence === 'high') return 'High confidence match - citation verified';
    if (confidence === 'medium') return 'Medium confidence match - verify manually';
    return 'Low confidence match - may not be exact';
  };

  return (
    <Card className="evidence-card border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" data-testid="evidence-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-base leading-tight">
                {card.study_name || 'Unnamed Study'}
              </h4>
              {isVerified && (
                <span title={getConfidenceTitle()} className={getConfidenceColor()}>
                  <CheckCircle className="h-4 w-4" />
                </span>
              )}
              {!isVerified && (
                <span title="Citation could not be verified - link goes to Google Scholar search" className="text-slate-400 dark:text-slate-500">
                  <AlertCircle className="h-4 w-4" />
                </span>
              )}
            </div>
            {/* Show verified title if different from AI-generated title */}
            {isVerified && card.verified_title && card.verified_title.toLowerCase() !== card.study_name?.toLowerCase() && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                Verified: {card.verified_title}
              </p>
            )}
          </div>
          <Badge className={`${getGradeStyle(card.grade)} px-2.5 py-1 text-xs font-bold uppercase tracking-wide shrink-0`}>
            <Award className="h-3 w-3 mr-1" />
            {getGradeLabel(card.grade)}
          </Badge>
        </div>
        
        <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-4">
          {card.claim || 'No claim provided'}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              {card.source || 'Unknown Source'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {card.year || 'N/A'}
            </span>
          </div>
          
          {primaryUrl && (
            <a
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1 text-xs font-medium ${
                isVerified 
                  ? 'text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300' 
                  : 'text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300'
              }`}
              data-testid="evidence-link"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {linkLabel}
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EvidenceCard;
